import React from 'react';
import {defineMessages} from 'react-intl';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {getTodayDateRange} from '@igp/shared/utils';

import type {PayoutReportColumns, PayoutReportViewModelKeys} from '@features/payout-report';
import {
    PayoutReportDataGrid,
    PayoutReportDownloadCsvButton,
    payoutReportMappingKeys,
    PayoutReportQueryFilters,
    usePayoutReport,
    usePayoutReportColumnSettings,
} from '@features/payout-report';

const localized = defineMessages({
    title: {
        id: 'PayoutReport_title',
        defaultMessage: 'Payout Report',
    },
});

function PayoutReportInner() {
    const mapper = useAutoMapper();
    const viewType: ViewType = 'PayoutReport';
    const pinnedColumns: PayoutReportColumns[] = ['player_uid', 'player_username', 'agent_uid', 'agent_username'];
    const columns: PayoutReportColumns[] = [
        ...pinnedColumns,
        'country',
        'poker_cash',
        'poker_mtt',
        'poker_plo',
        'casino',
        'global_spins',
        'sports',
        'total_share',
    ];

    const vmKeys: PayoutReportViewModelKeys[] = mapper.mapArray<PayoutReportColumns, PayoutReportViewModelKeys>(
        columns,
        payoutReportMappingKeys.columns,
        payoutReportMappingKeys.viewModelsKeys
    );

    const todayDateRange = getTodayDateRange();
    const {
        items,
        rawItems,
        totalCount,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        searchFilter,
        filterString,
    } = usePayoutReport({
        fields: vmKeys,
        viewType,
        defaultPaging: {page: 1, pageSize: 25},
        defaultFilters: [
            {key: 'transaction_date_from', value: todayDateRange[0]},
            {key: 'transaction_date_to', value: todayDateRange[1]},
        ],
        defaultSorting: [{field: 'player_uid', sort: 'desc'}],
        validateFilter: () => true,
        syncWithUrl: true,
    });

    const {visibleColumns, onVisibleColumnsChange} = usePayoutReportColumnSettings({
        gridId: 'PayoutReport',
        defaultColumns: columns,
        pinnedColumns,
    });

    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <PayoutReportDownloadCsvButton
                            columns={visibleColumns}
                            filter={filterString}
                            filename={localized.title}
                            total={totalCount}
                            mode="server"
                        />
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems streched>
                            <PayoutReportQueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['player_and_agent_uid_username', 'countries', 'transactionDate']}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <PayoutReportDataGrid
                    configurableColumns={{pinnedColumns, visibleColumns, onVisibleColumnsChange}}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter?.sorting}}
                    onSortChange={handleSortChange}
                />
            }
        />
    );
}

export const PayoutReport = withTraceErrorBoundary(PayoutReportInner, ErrorModal);
